// ===========================================
// Invoices Management Page
// ===========================================
// Admin interface for managing invoices and payments

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useSchoolTimezone } from '../../contexts/TenantContext';
import { usePermissions } from '../../hooks/usePermissions';
import { canAmendInvoice, checkoutLockMessage, isCheckoutLive } from '../../utils/invoiceAmend';
import PageHeader from '../../components/common/PageHeader';
import { formatUTCDate } from '../../utils/dateUtils';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useTerms } from '../../hooks/useAdmin';
import { useFamilies } from '../../hooks/useUsers';
import {
  useAdminInvoices,
  useInvoiceStatistics,
  useSendInvoice,
  useSendAllInvoices,
  useDeleteInvoice,
} from '../../hooks/useInvoices';
import {
  Invoice,
  InvoiceFilters,
  InvoiceStatus,
} from '../../api/invoices.api';
import { formatCurrencyFromDollars } from '../../utils/currency';
import { InvoiceStatusChip } from '../../components/invoices/InvoiceStatusChip';
import GenerateInvoicesDialog from '../../components/invoices/GenerateInvoicesDialog';
import CreateInvoiceDialog, { type CreateInvoiceInitial } from '../../components/invoices/CreateInvoiceDialog';
import EditInvoiceDialog from '../../components/invoices/EditInvoiceDialog';
import RecordPaymentDialog from '../../components/invoices/RecordPaymentDialog';

// ===========================================
// CONSTANTS
// ===========================================

const STATUS_OPTIONS: { value: InvoiceStatus | ''; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SENT', label: 'Unpaid' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'CANCELLED', label: 'Void' },
];

// ===========================================
// HELPER FUNCTIONS
// ===========================================

// Invoice money is stored in dollars.
const formatCurrency = formatCurrencyFromDollars;

function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString));
}


// ===========================================
// COMPONENT
// ===========================================

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { schoolSlug } = useParams<{ schoolSlug: string }>();

  // Filters state
  const [filters, setFilters] = useState<InvoiceFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog states
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Create Invoice deep link (`?create=1&familyId=…&termId=…&studentId=…&enrollmentId=…`, from
  // the "already invoiced" enrol prompt). Read ONCE, then cleared with replace so a refresh or
  // Back does not reopen the dialog. Opened once the families and terms have loaded, so the
  // dialog can tell "not found" from "not loaded yet".
  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingCreate, setPendingCreate] = useState<CreateInvoiceInitial | null>(null);
  const [createInitial, setCreateInitial] = useState<CreateInvoiceInitial | null>(null);
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    const familyId = searchParams.get('familyId') ?? '';
    const termId = searchParams.get('termId') ?? '';
    setPendingCreate({
      familyId,
      termId,
      studentId: searchParams.get('studentId') ?? undefined,
      enrollmentId: searchParams.get('enrollmentId') ?? undefined,
    });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Amend a SENT invoice (2026-09-26) — a snapshot, like selectedInvoice.
  const [amendTarget, setAmendTarget] = useState<Invoice | null>(null);
  const timeZone = useSchoolTimezone();
  const { can } = usePermissions();
  // Re-render when the soonest checkout lock lapses, so its Amend icon re-enables.
  const [now, setNow] = useState(() => Date.now());
  const [recordPaymentDialogOpen, setRecordPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [invoiceToSend, setInvoiceToSend] = useState<Invoice | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [sendAllDialogOpen, setSendAllDialogOpen] = useState(false);

  // Data queries
  const { data: invoicesData, isLoading, error } = useAdminInvoices(filters);
  const { data: statisticsData } = useInvoiceStatistics();
  const { data: termsData, isError: termsError } = useTerms();
  const { data: familiesData, isError: familiesError } = useFamilies();

  // Memoize to avoid reference changes affecting downstream useMemo
  const invoices = useMemo(() => invoicesData ?? [], [invoicesData]);

  // The soonest live checkout lock: tick the clock just after it lapses.
  const nextCheckoutExpiry = useMemo(() => {
    const times = invoices
      .map((i) => (i.checkoutExpiresAt ? new Date(i.checkoutExpiresAt).getTime() : NaN))
      .filter((t) => t > now);
    return times.length > 0 ? Math.min(...times) : null;
  }, [invoices, now]);
  useEffect(() => {
    if (nextCheckoutExpiry === null) return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.min(nextCheckoutExpiry - Date.now() + 1000, 2 ** 31 - 1)
    );
    return () => clearTimeout(timer);
  }, [nextCheckoutExpiry]);
  const statistics = statisticsData;
  const terms = termsData ?? [];
  const families = familiesData ?? [];

  useEffect(() => {
    // A failed list still opens the dialog, whose notice says the family or term was not found
    // (never a silent wait).
    if (!pendingCreate || !(termsData || termsError) || !(familiesData || familiesError)) return;
    setCreateInitial(pendingCreate);
    setCreateDialogOpen(true);
    setPendingCreate(null);
  }, [pendingCreate, termsData, familiesData, termsError, familiesError]);

  // Mutations
  const sendMutation = useSendInvoice();
  const sendAllMutation = useSendAllInvoices();
  const deleteMutation = useDeleteInvoice();

  // Filter invoices by search query
  const filteredInvoices = useMemo(() => {
    if (!searchQuery.trim()) return invoices;

    const query = searchQuery.toLowerCase();
    return invoices.filter(
      (invoice) =>
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.Family.name.toLowerCase().includes(query) ||
        invoice.description?.toLowerCase().includes(query)
    );
  }, [invoices, searchQuery]);

  // Get draft invoices for "Send All" feature
  const draftInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.status === 'DRAFT'),
    [invoices]
  );

  // Handle filter changes
  const handleFilterChange = (field: keyof InvoiceFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  // Handle send invoice
  const handleSendInvoice = async () => {
    if (!invoiceToSend) return;
    await sendMutation.mutateAsync(invoiceToSend.id);
    setSendDialogOpen(false);
    setInvoiceToSend(null);
  };

  // Handle record payment
  const handleOpenRecordPayment = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setRecordPaymentDialogOpen(true);
  };

  // Handle edit invoice
  const handleOpenEditInvoice = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setEditDialogOpen(true);
  };

  // Handle delete invoice
  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    await deleteMutation.mutateAsync(invoiceToDelete.id);
    setDeleteDialogOpen(false);
    setInvoiceToDelete(null);
  };

  // Handle send all draft invoices
  const handleSendAllInvoices = async () => {
    if (draftInvoices.length === 0) return;
    await sendAllMutation.mutateAsync();
    setSendAllDialogOpen(false);
  };

  // Table columns
  const columns: Column<Invoice>[] = [
    {
      id: 'invoiceNumber',
      label: 'Invoice #',
      format: (_value, invoice) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => navigate(`/${schoolSlug}/admin/invoices/${invoice.id}`)}
        >
          {invoice.invoiceNumber}
          {/* #376: a class on this invoice billed nothing (or its band weeks were left off). */}
          {(invoice.openNotBilledCount ?? 0) > 0 && (
            <Tooltip
              title={`${invoice.openNotBilledCount} class(es) not billed on this invoice: open it to see why`}
            >
              <Box
                component="span"
                data-testid="not-billed-marker"
                // A name on a plain span is not announced; as an image it is ("2 not billed").
                role="img"
                aria-label={`${invoice.openNotBilledCount} not billed`}
                sx={{ ml: 0.5, color: 'warning.main', fontWeight: 700 }}
              >
                *
              </Box>
            </Tooltip>
          )}
        </Typography>
      ),
    },
    {
      id: 'family',
      label: 'Family',
      format: (_value, invoice) => invoice.Family.name,
    },
    {
      id: 'total',
      label: 'Total',
      format: (_value, invoice) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatCurrency(invoice.total)}
        </Typography>
      ),
    },
    {
      id: 'amountPaid',
      label: 'Paid',
      format: (_value, invoice) => {
        const paid = parseFloat(invoice.amountPaid);
        const total = parseFloat(invoice.total);
        if (paid === 0) return '-';
        return (
          <Typography
            variant="body2"
            sx={{ color: paid >= total ? 'success.main' : 'warning.main' }}
          >
            {formatCurrency(invoice.amountPaid)}
          </Typography>
        );
      },
    },
    {
      id: 'dueDate',
      label: 'Due Date',
      format: (_value, invoice) => {
        const isOverdue =
          invoice.status !== 'PAID' &&
          invoice.status !== 'CANCELLED' &&
          new Date(invoice.dueDate) < new Date();
        return (
          <Typography
            variant="body2"
            sx={{ color: isOverdue ? 'error.main' : 'text.primary' }}
          >
            {formatUTCDate(invoice.dueDate)}
          </Typography>
        );
      },
    },
    {
      id: 'sentAt',
      label: 'Email Sent',
      format: (_value, invoice) => {
        if (!invoice.sentAt) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MarkEmailReadIcon fontSize="small" sx={{ color: '#4580E4', flexShrink: 0 }} />
            <Box>
              <Typography variant="body2">{formatDate(invoice.sentAt)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit' }).format(new Date(invoice.sentAt))}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      id: 'status',
      label: 'Status',
      format: (_value, invoice) => <InvoiceStatusChip invoice={invoice} />,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'center',
      sortable: false,
      minWidth: 150,
      format: (_value, invoice) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              component={Link}
              to={`/${schoolSlug}/admin/invoices/${invoice.id}`}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {invoice.status === 'DRAFT' && (
            <>
              <Tooltip title="Edit Invoice">
                <IconButton
                  size="small"
                  onClick={() => handleOpenEditInvoice(invoice)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send Invoice">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setInvoiceToSend(invoice);
                    setSendDialogOpen(true);
                  }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Invoice">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setInvoiceToDelete(invoice);
                    setDeleteDialogOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {canAmendInvoice(invoice) && can('operations_invoices', 'write') && (
            // Amend a sent invoice until its first payment. Locked (with the
            // reason in the tooltip) while the family is on Stripe checkout.
            <Tooltip
              title={isCheckoutLive(invoice, new Date(now)) ? checkoutLockMessage(invoice, timeZone) : 'Amend Invoice'}
            >
              <span tabIndex={isCheckoutLive(invoice, new Date(now)) ? 0 : undefined}>
                <IconButton
                  size="small"
                  aria-label="Amend invoice"
                  disabled={isCheckoutLive(invoice, new Date(now))}
                  onClick={() => setAmendTarget(invoice)}
                >
                  <EditNoteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {(invoice.status === 'SENT' ||
            invoice.status === 'PARTIALLY_PAID' ||
            invoice.status === 'OVERDUE') && (
            // Same dialog as the invoice details page: amount, date of
            // payment, method, reference. (Replaced a one-click shortcut
            // that recorded the balance as OTHER, dated today.)
            <Tooltip title="Record Payment">
              <IconButton
                size="small"
                color="success"
                aria-label="Record payment"
                onClick={() => handleOpenRecordPayment(invoice)}
              >
                <PaymentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  // Render statistics cards
  const renderStatistics = () => {
    if (!statistics) return null;

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FCF6E6' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Outstanding
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#4580E4' }}>
                {formatCurrency(statistics.totalOutstanding)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFAE9E' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Overdue
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#ff4040' }}>
                {formatCurrency(statistics.totalOverdue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Paid This Month
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#96DAC9' }}>
                {statistics.invoicesByStatus.PAID || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Pending
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {(statistics.invoicesByStatus.SENT || 0) +
                  (statistics.invoicesByStatus.PARTIALLY_PAID || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load invoices. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Invoices"
        subtitle="Manage invoices and payments"
        action={
          // Phone: Create Invoice first on its own full-width row, the rest share the rows below
          // (labels never wrap). From sm up: one row in source order, as before.
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              // flexGrow only: a `flex` shorthand here would out-specify Create Invoice's own flexBasis.
              '& .MuiButton-root': { whiteSpace: 'nowrap', flexGrow: { xs: 1, sm: 0 } },
            }}
          >
            <Button
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={() => setGenerateDialogOpen(true)}
            >
              Auto Generate Invoices
            </Button>
            {draftInvoices.length > 0 && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<SendIcon />}
                onClick={() => setSendAllDialogOpen(true)}
              >
                Send All ({draftInvoices.length})
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ order: { xs: -1, sm: 0 }, flexBasis: { xs: '100%', sm: 'auto' } }}
            >
              Create Invoice
            </Button>
          </Box>
        }
      />

      {/* Statistics */}
      {renderStatistics()}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={(e: SelectChangeEvent) =>
                  handleFilterChange('status', e.target.value)
                }
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Term</InputLabel>
              <Select
                value={filters.termId || ''}
                label="Term"
                onChange={(e: SelectChangeEvent) =>
                  handleFilterChange('termId', e.target.value)
                }
              >
                <MenuItem value="">All Terms</MenuItem>
                {terms.map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Family</InputLabel>
              <Select
                value={filters.familyId || ''}
                label="Family"
                onChange={(e: SelectChangeEvent) =>
                  handleFilterChange('familyId', e.target.value)
                }
              >
                <MenuItem value="">All Families</MenuItem>
                {families.map((family) => (
                  <MenuItem key={family.id} value={family.id}>
                    {family.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={filteredInvoices}
        loading={isLoading}
        searchable={false}
        emptyMessage="No invoices found"
        noWrapOnMobile
      />

      {/* Generate Invoices Dialog */}
      <GenerateInvoicesDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        terms={terms}
        families={families}
      />

      {/* Create Invoice Dialog */}
      <CreateInvoiceDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateInitial(null);
        }}
        terms={terms}
        families={families}
        initial={createInitial}
      />

      {/* Edit Invoice Dialog */}
      <EditInvoiceDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        // A draft edit can add/remove discounts (2026-10-02). Read is separate: the
        // saved-discount list needs it, and write does not imply it.
        canApplyDiscount={can('operations_pricing', 'write')}
        canRemoveDiscount={can('operations_pricing', 'delete')}
        canReadPricing={can('operations_pricing', 'read')}
      />

      <EditInvoiceDialog
        open={amendTarget !== null}
        onClose={() => setAmendTarget(null)}
        invoice={amendTarget}
        mode="amend"
        schoolTimeZone={timeZone}
        canApplyDiscount={can('operations_pricing', 'write')}
        canRemoveDiscount={can('operations_pricing', 'delete')}
        // #625 (/qa phase 5 M2): the school default coverage and the category list need it.
        canReadPricing={can('operations_pricing', 'read')}
      />

      {/* Record Payment Dialog */}
      <RecordPaymentDialog
        open={recordPaymentDialogOpen}
        onClose={() => {
          setRecordPaymentDialogOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
      />

      {/* Send Invoice Confirmation */}
      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Invoice"
        message={`Are you sure you want to send invoice ${invoiceToSend?.invoiceNumber} to ${invoiceToSend?.Family.name}? They will receive an email notification.`}
        confirmLabel="Send"
        onConfirm={handleSendInvoice}
        onCancel={() => {
          setSendDialogOpen(false);
          setInvoiceToSend(null);
        }}
        loading={sendMutation.isPending}
      />

      {/* Delete Invoice Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Invoice"
        message={`Are you sure you want to delete invoice ${invoiceToDelete?.invoiceNumber}? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteInvoice}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setInvoiceToDelete(null);
        }}
        loading={deleteMutation.isPending}
      />

      {/* Send All Invoices Confirmation */}
      <ConfirmDialog
        open={sendAllDialogOpen}
        title="Send All Draft Invoices"
        message={`Are you sure you want to send all ${draftInvoices.length} draft invoice(s)? Each family will receive an email notification.`}
        confirmLabel="Send All"
        onConfirm={handleSendAllInvoices}
        onCancel={() => {
          if (!sendAllMutation.isPending) {
            setSendAllDialogOpen(false);
          }
        }}
        loading={sendAllMutation.isPending}
      />
    </Box>
  );
}
